<template>
  <div
    v-if="hasError"
    class="mb-1 mx-1 mb-sm-4 mx-sm-4 toast show position-absolute bottom-0 bg-danger-subtle"
  >
    <div class="toast-header">
      <strong class="me-auto text-danger-emphasis">{{
        $t("app.error_occurred")
      }}</strong>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="toast"
        aria-label="Close"
        @click="clearError"
      ></button>
    </div>
    <div class="toast-body">
      <template v-if="errorCode !== ''">
        {{ $t("app.error_message_code", { errorCode: errorCode }) }}
        <br />
      </template>
      {{ $t("app.contact_support") }}
    </div>
  </div>
  <div
    v-if="warningMessage !== ''"
    class="mb-1 mx-1 mb-sm-4 mx-sm-4 toast show position-absolute bottom-0 bg-warning-subtle"
  >
    <div class="toast-header">
      <strong class="me-auto text-warning-emphasis">{{ warningTitle }}</strong>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="toast"
        aria-label="Close"
        @click="clearWarning"
      ></button>
    </div>
    <div class="toast-body">
      {{ warningMessage }}
    </div>
  </div>
  <div
    v-if="successMessage !== ''"
    class="mb-1 mx-1 mb-sm-4 mx-sm-4 toast show position-absolute bottom-0 bg-success-subtle"
  >
    <div class="toast-header">
      <strong class="me-auto text-success-emphasis">{{ successTitle }}</strong>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="toast"
        aria-label="Close"
        @click="clearSuccess"
      ></button>
    </div>
    <div class="toast-body">
      {{ successMessage }}
    </div>
  </div>
  <RouterView />
  <GlobalLoadingOverlay />
</template>

<script setup lang="ts">
import { provide, ref } from "vue";
import GlobalLoadingOverlay from "@/components/common/GlobalLoadingOverlay.vue";
import { useStrayFileDropGuard } from "@/composables/useStrayFileDropGuard";
import logger from "./logger";

useStrayFileDropGuard();

const hasError = ref(false);
const errorCode = ref("");

const clearError = () => {
  hasError.value = false;
  errorCode.value = "";
};

const handleError = (error: unknown) => {
  logger.error("Error: ", error);
  hasError.value = true;

  if (typeof error === "string") {
    errorCode.value = "-1";
    return;
  }

  // Callers pass either the axios error or its response body directly, so both shapes land here.
  // An Error is never the body: axios stamps its own transport codes (ERR_NETWORK) there, and those
  // appear in no server log, so quoting one to support would be a dead end.
  const body =
    (error as { response?: { data?: unknown } })?.response?.data ??
    (error instanceof Error ? undefined : error);
  const code = (body as { code?: unknown })?.code;
  errorCode.value = typeof code === "string" ? code : "";
};

const warningMessage = ref("");
const warningTitle = ref("");

const clearWarning = () => {
  warningMessage.value = "";
  warningTitle.value = "";
};

const handleWarning = (title: string, description: string) => {
  logger.warn("Warning: ", { title, description });
  warningTitle.value = title;
  warningMessage.value = description;
};

const successMessage = ref("");
const successTitle = ref("");

const clearSuccess = () => {
  successMessage.value = "";
  successTitle.value = "";
};

const handleSuccess = (title: string, description: string) => {
  logger.info("Success: ", { title, description });
  successTitle.value = title;
  successMessage.value = description;
};

provide("globalErrorHandler", handleError);
provide("globalWarningHandler", handleWarning);
provide("globalSuccessHandler", handleSuccess);
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

html,
body,
#app {
  height: 100%;
  margin: 0;
  padding: 0;
}

.navbar {
  margin-bottom: 0px;
  flex-shrink: 0;
  padding: 1rem;
  border: 0;
  .navbar-logo {
    height: 32px; /* Adjust size as needed */
    margin-right: 10px;
  }
  .container-fluid {
    padding: 0;
  }
}

.toast {
  z-index: var(--phoenix-toast-zindex);
}

.close-btn {
  background: none;
  border: none;
  color: white;
  margin-left: 10px;
  cursor: pointer;
}

.alert {
  margin-bottom: 0;
  flex-shrink: 0;
  border-radius: 0;
  border-width: 5px;
  border-style: dashed;
  padding: 1rem;
}

/* Adjust font size for devices with screen width up to 375px and a standard device pixel ratio */
@media only screen and (max-width: 376px) and (-webkit-device-pixel-ratio: 2),
  only screen and (max-width: 376px) and (min-resolution: 192dpi) {
  body {
    font-size: 14px; /* Smaller font size for smaller screens like iPhone SE */
  }
}
</style>
